import React, {useState, useEffect} from 'react';
import Modal, { useModal } from '../../Components/Modal';
import { usePaymentsAccount } from '../../Data/Payments';
import { stripeLiveKey, stripeTestKey } from '../../Services/Keys';
import Alert from '../../Components/Alert';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements  } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CommonFieldWrapper from '../../Components/Form/Fields/CommonFieldWrapper';
import ButtonRow from '../../Components/Button/ButtonRow';
import Button from '../../Components/Button/Button';
import { useToasts } from '../../Components/Toasts';
import API from '../../Services/API';
import ButtonGap from '../../Components/Button/ButtonGap';
import { useSession } from '../../Components/Session';
import { useDialogues } from '../../Components/Dialogues';
import { PageLoading } from '../../Components/Loading';

let stripePromise = null;

const AddCard = ({isOpen, setIsOpen, onComplete}) => {
    const {account} = usePaymentsAccount(true);

    useEffect(() => {
        if (!account || stripePromise) {
            return;
        }
        stripePromise = loadStripe(
            account.providerTestMode ? stripeTestKey : stripeLiveKey
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, stripePromise]);

    return <Modal title="Add card" isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        {(isOpen && stripePromise && account) ? <Elements stripe={stripePromise}>
            <Inner {...{stripePromise, account, onComplete}} />
        </Elements> : <PageLoading />}
    </Modal>
}

const Inner = ({account, onComplete}) => {
    const {closeModal} = useModal();
    const {showError, showSuccess} = useToasts();
    const [clientSecret, setClientSecret] = useState(null);
    const [locked, setLocked] = useState(false);
    const [removing, setRemoving] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const {balance} = useSession();
    const {confirm} = useDialogues();

    useEffect(() => {
        API.get('balance/setupIntent').then(result => {
            setClientSecret(result.client_secret)
        }).catch(error => {
            showError(API.parseErrorResponse(error))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSubmit = async (e) => {
        e && e.preventDefault();

        setLocked(true);

        const result = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: elements.getElement(CardNumberElement)
            }
        });

        if (result.error) {
            showError(result.error.message)
            setLocked(false);
        } else {
            API.post('balance/setupIntent', result).then(() => {
                setLocked(false);
                showSuccess('Card added');
                onComplete && onComplete()
                closeModal();
            })
        }
    }

    const removePaymentMethod = async () => {
        setRemoving(true);

        API.delete('balance/paymentMethod').then(() => {
            showSuccess('Card removed');
            onComplete && onComplete()
            closeModal();
        }).catch((err) => {
            setRemoving(false);
            showError(API.parseErrorResponse(err));
        })
    }

    const options = {
        disabled: locked || removing,
        style: {
            base: {
                color: '#495057',
                fontSize: '1.1rem',
                lineHeight: '2rem'
            }
        }
    }

    const classes =  (locked || removing) ? 'py-1 bg-gray-100 text-gray-400' : 'py-1 bg-white text-gray-900';

    return <form onSubmit={handleSubmit}>
        {account.providerTestMode && <Alert theme="info"
            title="Test mode"
            message="4242 4242 4242 4242 - 12/34 - 123"
        />}

        <CommonFieldWrapper label="Card number" isDisabled={locked || removing}>
            <CardNumberElement className={classes} />
        </CommonFieldWrapper>

        <div className="grid grid-cols-2 gap-4 mt-4">
            <CommonFieldWrapper label="Card expiry date"
                isDisabled={locked || removing}
                className={`${classes} w-[50%]`}
                >
                <CardExpiryElement options={options} className={classes} />
            </CommonFieldWrapper>

            <CommonFieldWrapper label="Security number"
                isDisabled={locked || removing}
                className={`${classes} w-[50%]`}
                help="The last 3 digits on the back of your card"
            >
                <CardCvcElement options={options} className={classes} />
            </CommonFieldWrapper>
        </div>

        <ButtonRow>
            <Button theme="lightDanger"
                label="Remove"
                onClick={() => confirm(
                    {
                        theme: 'danger',
                        title: 'Remove card',
                        message: 'Are you sure you want to remove your payment card?',
                        confirmLabel: 'Remove',
                    },
                    () => removePaymentMethod()
                )}
                loading={removing}
                hide={!balance?.paymentMethod}
            />
            <ButtonGap />
            <Button theme="light"
                label="Cancel"
                onClick={() => closeModal()}
                disabled={locked || removing}
            />
            <Button theme="primary"
                label="Add"
                onClick={() => handleSubmit()}
                loading={locked}
                disabled={removing}
            />
        </ButtonRow>
    </form>
}

export default AddCard;
